import React from 'react';
import type { TeamSecretPolicyV1 } from '../client';

interface SecretPolicyEditorProps {
  policy: TeamSecretPolicyV1;
  onChange: (policy: TeamSecretPolicyV1) => void;
  disabled?: boolean;
}

const lines = (value: string) =>
  value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const SecretPolicyEditor: React.FC<SecretPolicyEditorProps> = ({
  policy,
  onChange,
  disabled,
}) => {
  const updateWorkspace = (next: Partial<TeamSecretPolicyV1['workspace']>) =>
    onChange({
      ...policy,
      workspace: { ...policy.workspace, ...next },
    });
  const updateBrowser = (next: Partial<TeamSecretPolicyV1['browser']>) =>
    onChange({ ...policy, browser: { ...policy.browser, ...next } });

  return (
    <fieldset className="secret-policy-editor" disabled={disabled}>
      <legend>Allowed destinations</legend>
      <p className="descriptionText">
        Each enabled mode is limited to the destinations listed here.
      </p>
      <div className="secret-policy-editor-grid">
        <section className="secret-policy-mode">
          <label className="secret-mode-toggle">
            <input
              type="checkbox"
              checked={policy.workspace.enabled}
              onChange={(event) =>
                updateWorkspace({
                  enabled: event.target.checked,
                  allowedHosts: event.target.checked
                    ? policy.workspace.allowedHosts
                    : [],
                })
              }
            />
            <span>
              <strong>Workspace</strong>
              <small>
                Environment variable injection for selected commands
              </small>
            </span>
          </label>
          <label htmlFor="secret-workspace-hosts">
            Allowed workspace hosts
          </label>
          <textarea
            id="secret-workspace-hosts"
            rows={3}
            value={policy.workspace.allowedHosts.join('\n')}
            disabled={!policy.workspace.enabled || disabled}
            placeholder="api.example.com"
            onChange={(event) =>
              updateWorkspace({ allowedHosts: lines(event.target.value) })
            }
          />
          <small>One public hostname or leading wildcard per line.</small>
        </section>
        <section className="secret-policy-mode">
          <label className="secret-mode-toggle">
            <input
              type="checkbox"
              checked={policy.browser.enabled}
              onChange={(event) =>
                updateBrowser({
                  enabled: event.target.checked,
                  allowedOrigins: event.target.checked
                    ? policy.browser.allowedOrigins
                    : [],
                })
              }
            />
            <span>
              <strong>Browser</strong>
              <small>Password filling on verified origins</small>
            </span>
          </label>
          <label htmlFor="secret-browser-origins">
            Allowed browser origins
          </label>
          <textarea
            id="secret-browser-origins"
            rows={3}
            value={policy.browser.allowedOrigins.join('\n')}
            disabled={!policy.browser.enabled || disabled}
            placeholder="https://example.com"
            onChange={(event) =>
              updateBrowser({ allowedOrigins: lines(event.target.value) })
            }
          />
          <small>One exact HTTPS origin per line.</small>
        </section>
      </div>
    </fieldset>
  );
};

export default SecretPolicyEditor;
